import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import JsonLd from "../components/JsonLd";
import { WHATSAPP } from "../data/rooms";
import { pic } from "../lib/pic";

const SOBER_NOMADS = "https://www.facebook.com/groups/1537243484868294";
const LINKEDIN = "https://www.linkedin.com/in/stevehaynes";

const Kicker = ({ children, className = "", style }) => (
  <p className={`font-label text-xs tracking-[0.3em] uppercase ${className}`} style={style}>{children}</p>
);
const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mt-14 mb-5">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[#2E2A26]/75 text-base sm:text-lg leading-relaxed mb-6">{children}</p>
);
const PullQuote = ({ children }) => (
  <blockquote className="border-l-2 border-[#C85A38] pl-6 my-10">
    <p className="font-serif italic text-2xl sm:text-3xl text-[#2E2A26] leading-snug">{children}</p>
  </blockquote>
);

export default function LondonPage() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Speech I'd Give at the Coliving Conference (If Someone Handed Me a Microphone)",
    "author": { "@type": "Person", "name": "Steve Haynes", "url": "https://www.savicoliving.com/about" },
    "publisher": { "@type": "Organization", "name": "Savi Coliving", "url": "https://www.savicoliving.com" },
    "url": "https://www.savicoliving.com/london",
    "image": "https://www.savicoliving.com/images/villa-aerial.jpg",
    "about": ["coliving industry", "coliving conference", "community", "coliving Spain"]
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>The Speech I'd Give at the Coliving Conference | Savi Coliving</title>
        <meta name="description" content="Coliving raised $4.1bn last year. Steve Haynes runs 16 rooms in the Spanish mountains. Notes from a very small operator to a very large industry, ahead of the Coliving Conference in London." />
        <meta property="og:title" content="The Speech I'd Give at the Coliving Conference (If Someone Handed Me a Microphone)" />
        <meta property="og:description" content="16 rooms. 89 guests. 26 countries. Zero drinks. What a small house in the mountains learned that $4 billion hasn't yet." />
        <meta property="og:image" content="https://www.savicoliving.com/images/villa-aerial.jpg" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://www.savicoliving.com/london" />
      </Helmet>
      <JsonLd data={articleSchema} />

      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="pt-16" data-testid="london-hero">
        <div className="relative min-h-[62vh] sm:min-h-[72vh] flex items-end overflow-hidden">
          <img
            {...pic("villa-aerial")}
            fetchpriority="high" decoding="async"
            alt="Aerial view of the Savi Coliving villa in the Sierra Calderona"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="london-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E2A26]/30 via-[#2E2A26]/55 to-[#2E2A26]/95" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20 pt-40 text-center">
            <Kicker className="text-[#E8DAC8] mb-6" style={{textShadow: "0 1px 10px rgba(0,0,0,0.7)"}}>Notes before the Coliving Conference · London, 14 October</Kicker>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#FBF7F1] leading-tight mb-6" style={{textShadow: '0 2px 16px rgba(0,0,0,0.45)'}}>
              The speech I'd give at the Coliving Conference<br className="hidden sm:block" />
              <span className="italic text-[#C89B7B]">(if someone handed me a microphone).</span>
            </h1>
            <p className="text-[#FBF7F1]/70 text-sm">
              By Steve Haynes · 16 rooms, 89 guests, 26 countries, zero drinks · Sierra Calderona, Valencia
            </p>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20" data-testid="london-article">
        <P>
          <span className="font-serif text-[#C85A38] text-6xl float-left leading-[0.8] mr-3 mt-1">I</span>
          n a few weeks I'll be in a room in London with several hundred people who run coliving buildings, fund coliving buildings, or sell software to people who run coliving buildings. Savi is a finalist at the Coliving Awards, so I've got a lanyard and a seat and, if the schedule holds, roughly zero minutes of stage time.
        </P>
        <P>
          Which is fair. I run sixteen rooms in the Spanish mountains. Some of the people in that room run sixteen <em>thousand</em>. I will be the one who smells faintly of dog.
        </P>
        <P>
          But I've been rehearsing a speech in the car on the way to the supermarket anyway, the way you do, and it seems a waste not to write it down. So here it is. The speech I'd give if someone handed me a microphone. Five minutes, no slides, one podenco.
        </P>

        <H2>First, the good news, because it is good news</H2>
        <P>
          Coliving took in $4.1 billion of institutional money last year. Nearly double the year before. There are something like 169,000 coliving beds in Europe now, and half the property investors in the Savills survey say they want more of them. Two years ago I was explaining to my mum what coliving was. Now there are men in gilets explaining it to me.
        </P>
        <P>
          I want to be clear about this before I say anything else: I think that's brilliant. Lonely people in expensive cities are a real problem, and for once the money is pointed at it. I'm not here to tell a room full of people who've worked very hard that they're doing it wrong.
        </P>
        <P>I'm here to tell them what the money can't see from where it's standing.</P>

        <H2>The number I'd put on my first slide, if I had slides</H2>
        <P>
          It isn't occupancy. We've run at 95 to 98 percent since we opened, and I'm proud of that, but occupancy only tells you the marketing works. It doesn't tell you what happened to anyone once they got here.
        </P>
        <P>
          The number I actually watch is what I've started calling the Wednesday Test. A new guest arrives on a Monday. By Wednesday, does everyone in the house know their name? Not "the tall one in the Sky Room." Their name, what they're working on, and whether they take milk.
        </P>
        <PullQuote>
          If the answer's yes, the rest sorts itself out. If it's no, no events calendar on earth will fix it.
        </PullQuote>
        <P>
          Read the investor reports and the definition of a fundable coliving is now 200-plus beds, 24 months of audited numbers, gateway city. I understand why. You can't put a friendship on a balance sheet. But I've never once seen a Wednesday Test passed by 200 people, and I've stayed in a lot of buildings that tried.
        </P>

        <img
          {...pic("coworking", { sizes: "(min-width: 768px) 768px, 100vw", fallback: 768 })}
          alt="The coworking room at Savi Coliving, dual screens and mountain light"
          className="w-full rounded-2xl my-10 border border-stone-100"
          loading="lazy"
        />

        <H2>Three things that happened at our table this year</H2>
        <P>
          A woman in her late fifties arrived who'd never lived with anyone but her ex-husband. She booked a month "to see if I still know how to share a kitchen." She does. She also turned out to be terrifying at cards, and by the second week two developers half her age were losing to her nightly and loving it.
        </P>
        <P>
          A founder arrived who'd stopped drinking because it was quietly costing her companies. She said the thing she'd missed most wasn't the wine. It was having somewhere to be at eight o'clock on a Thursday that wasn't a bar. She extended twice.
        </P>
        <P>
          A bloke arrived with a guitar and asked, before he'd unpacked, whether anyone would mind. Nobody minded. That question, asked before he'd unpacked, told me everything I needed to know about how his month would go.
        </P>
        <P>
          None of those things scale. That's the point. They only happen in a house small enough that a question like "would anyone mind?" has an actual answer.
        </P>

        <H2>What I'd ask the room to do</H2>
        <P>
          Three things. Then I'd sit down, because the coffee's usually at the back.
        </P>
        <P>
          <strong>One. Measure the right thing.</strong> Occupancy tells you the marketing works. Renewals, returns and referrals tell you the community works. Only one of those survives a recession, and it isn't the one on the front of the deck.
        </P>
        <P>
          <strong>Two. Build houses, not floors.</strong> A 200-bed building can absolutely work. As ten houses of twenty that eat together, with their own kitchen and their own long table and their own arguments about the dishwasher. Not as one lobby and 200 doors and a "vibe."
        </P>
        <P>
          <strong>Three. Be a neighbour.</strong> The biggest nomad coliving in Asia shut this year. Not for lack of demand. It built a bubble, and the town outside couldn't think of a single reason to want it there. My test is simple: if the person who runs the local bakery can't tell you what your building does for them, you're on borrowed time. I'm not going to pretend we've perfected this in Náquera. But we're working on it, and it's on the list every week.
        </P>

        <img
          {...pic("pool", { sizes: "(min-width: 768px) 768px, 100vw", fallback: 768 })}
          alt="The pool at Savi Coliving seen through the arched veranda"
          className="w-full rounded-2xl my-10 border border-stone-100"
          loading="lazy"
        />

        <H2>The bit I'd say last</H2>
        <P>
          Two years ago I wrote that sobriety was never our product; presence was. I still think that's right, and I think it's true of the whole industry, not just the odd sober house in the hills.
        </P>
        <PullQuote>
          You can scale buildings. You can't scale presence. You can only make room for it, and then get out of the way.
        </PullQuote>
        <P>
          The capital arriving now is a vote of confidence in an idea: that people are lonely and would like not to be, and that a well-run building can help. Let's not spend it building the exact thing the idea was a reaction against.
        </P>
        <P>
          That's the speech. If you're in London on the 14th, come and find me. I'll be near the coffee, I'll be wearing the only jacket I own, and I'd genuinely like to hear what your Wednesday Test looks like.
        </P>
        <P>
          And if you're not in the industry at all and just read this because you're tired of eating dinner on your own in a beautiful place: the house has a long table, and there's usually a chair.
        </P>

        <figure className="my-10 max-w-sm mx-auto">
          <img src="/images/jenny.jpg" alt="Jenny the podenco at Savi Coliving" className="w-full rounded-2xl border border-stone-100" loading="lazy" />
          <figcaption className="text-center text-[#2E2A26]/40 text-xs mt-3 italic font-serif">Jenny. Not coming to London. Has opinions about it.</figcaption>
        </figure>

        {/* CTAs */}
        <div className="grid sm:grid-cols-2 gap-4 my-12" data-testid="london-ctas">
          <a
            href={LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-[#2E2A26] hover:bg-[#3a352f] text-white rounded-2xl p-6 text-center transition-colors"
            data-testid="london-linkedin-cta"
          >
            <p className="font-serif text-xl mb-1">See you in London?</p>
            <p className="text-white/75 text-sm">Say hello on LinkedIn and we'll find the coffee together</p>
          </a>
          <Link
            to="/#dates"
            className="block bg-[#C85A38] hover:bg-[#D06A4F] text-white rounded-2xl p-6 text-center transition-colors"
            data-testid="london-dates-cta"
          >
            <p className="font-serif text-xl mb-1">There's usually a chair</p>
            <p className="text-white/75 text-sm">Check dates and rooms at the house</p>
          </Link>
        </div>
        <p className="text-center text-sm text-[#2E2A26]/50">
          Sober or sober-curious and travelling? <a href={SOBER_NOMADS} target="_blank" rel="noopener noreferrer" className="text-[#5C6A4C] underline underline-offset-2">Sober Nomads</a> is our free community. Or just <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="text-[#C85A38] underline underline-offset-2">message Steve</a>.
        </p>

        <p className="font-serif italic text-2xl text-[#2E2A26] text-center mt-14">A clear mind, in good company.</p>
        <p className="text-center text-[#C85A38] font-serif text-lg mt-2">— Steve</p>
        <p className="text-center text-[#2E2A26]/40 text-xs mt-8 max-w-md mx-auto leading-relaxed">
          Steve Haynes is the founder of Savi Coliving, a sober coliving villa in the Sierra Calderona mountains near Valencia, Spain, and winner of Best Destination Coliving at the Coliving Awards 2026. He also advises owners and investors at The Coliving Advisor. Figures from State of Coliving 2026 (everythingcoliving.com) and the Savills European OpRe Investor Survey 2026.
        </p>
      </article>

      <PublicFooter />
    </div>
  );
}
